import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export interface WebhookConfig {
  _id?: string;
  id?: string;
  webhook_name?: string;
  webhook_url: string;
  webhook_trigger_events: string[]; // Changed to array
  webhook_method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  webhook_api_key_format: 'HEADER' | 'QUERY_STRING' | 'BODY';
  webhook_api_key: string;
  webhook_api_value: string;
  webhook_description?: string;
  webhook_content_type?: 'application/json' | 'application/xml' | 'application/x-www-form-urlencoded';
  webhook_priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  is_active: boolean;
  // Retry configuration
  retry_enabled?: boolean;
  retry_max_attempts?: number;
  retry_backoff_seconds?: number;
  timeout_seconds?: number;
  // Security/HMAC configuration
  webhook_secret?: string;
  include_signature?: boolean;
  signature_header?: string;
  // Stats
  retry_count?: number;
  last_triggered_at?: string;
  success_count?: number;
  failure_count?: number;
  total_calls?: number;
  success_rate?: number;
  created_at?: string;
  updated_at?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WebhookTriggerEvent {
  label: string;
  value: string;
}

declare global {
  interface WebhookTriggerEvent {
    label: string;
    value: string;
  }
}

export interface WebhookHistory {
  _id: string;
  webhook_id: string;
  trigger_type: string;
  status: 'SUCCESS' | 'FAILED' | 'PENDING' | 'TIMEOUT' | 'RETRYING';
  payload: any;
  response?: any;
  error_message?: string;
  http_status_code?: number;
  response_time_ms?: number;
  retry_attempt?: number;
  headers_sent?: Record<string, string>;
  createdAt: string;
}

class WebhookService {
  async getWebhookById(id: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WEBHOOKS.GET_BY_ID(id));
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch webhook');
    } catch (error) {
      console.error('Error fetching webhook:', error);
      throw error;
    }
  }

  async updateWebhook(id: string, webhookData: Partial<WebhookConfig>) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.WEBHOOKS.UPDATE(id), webhookData);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update webhook');
    } catch (error) {
      console.error('Error updating webhook:', error);
      throw error;
    }
  }

  async createWebhook(webhookData: Partial<WebhookConfig>) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.WEBHOOKS.CREATE, webhookData);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create webhook');
    } catch (error) {
      console.error('Error creating webhook:', error);
      throw error;
    }
  }

  async deleteWebhook(id: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.WEBHOOKS.DELETE(id));
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete webhook');
    } catch (error) {
      console.error('Error deleting webhook:', error);
      throw error;
    }
  }

  async getTriggerEvents(): Promise<WebhookTriggerEvent[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WEBHOOKS.TRIGGER_TYPES);
      if (response.data.success) {
        return response.data.data || [];
      }
      throw new Error(response.data.description || 'Failed to fetch trigger events');
    } catch (error) {
      console.error('Error fetching trigger events:', error);
      throw error;
    }
  }

  async getWebhooks(filters = { skip: 0, limit: 10 }) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.WEBHOOKS.LIST, filters);
      if (response.data.success) {
        const data = response.data.data;
        // Return paginated response with totalCount
        return {
          list: data.list || data,
          totalCount: data.totalCount || (data.list || data).length,
          last_modified: data.last_modified
        };
      }
      throw new Error(response.data.description || 'Failed to fetch webhooks');
    } catch (error: any) {
      console.error('Error fetching webhooks:', error);
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Failed to fetch webhooks';
      throw new Error(errorMessage);
    }
  }

  async testWebhook(id: string) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.WEBHOOKS.TEST(id));
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to test webhook');
    } catch (error) {
      console.error('Error testing webhook:', error);
      throw error;
    }
  }

  async getWebhookHistory(id: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WEBHOOKS.GET_HISTORY(id));
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch webhook history');
    } catch (error) {
      console.error('Error fetching webhook history:', error);
      throw error;
    }
  }
}

export const webhookService = new WebhookService();